import {
  Alert,
  Button,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ModalOverlay,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@compass/components'
import { contentStyles } from '@compass/Modal'
import { AppliedQueryFilters, QueryFilterDefinition } from 'components/QueryFilters'
import { formatDatetime } from 'features/commonUI'
import { getErrorParams } from 'helpers/data'
import { getClientTimezone } from 'helpers/locale'
import { compassRegister } from 'helpers/reactHookForm'
import { EMAIL_VALIDATION } from 'helpers/validation'
import { useToast } from 'hooks'
import { useCurrentUser } from 'hooks/api/users'
import { FileSpreadsheet } from 'lucide-react'
import { DateTime } from 'luxon'
import { ampli, DataExportRequestedProperties } from 'models/ampli'
import { useCallback, useMemo } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'

import { useBotDetectionEventsExport } from '../hooks'
import { BotDetectionEventsExportBody, BotDetectionEventsFilter } from '../models'
import { BotDetectionEventsFilterValues } from './useBotDetectionEventsFilter'

export interface ExportBotDetectionEventsModalProps {
  isOpen: boolean
  handleClose: () => void
  subscriptionId: string
  dateRange: {
    since: DateTime
    before: DateTime
  }
  filterDefinitions: Record<string, QueryFilterDefinition<BotDetectionEventsFilterValues>>
  filterValues: Partial<BotDetectionEventsFilterValues>
}

export function ExportBotDetectionEventsModal({
  isOpen,
  handleClose,
  subscriptionId,
  dateRange,
  filterDefinitions,
  filterValues,
}: ExportBotDetectionEventsModalProps) {
  const { data: currentUser } = useCurrentUser()
  const defaultLocalTz = currentUser?.timezone ?? getClientTimezone()
  const { mutate: initiateBotDetectionEventsExport, isPending } = useBotDetectionEventsExport()
  const { showToast } = useToast()

  const defaultValues = useMemo(
    () => ({
      timeZone: defaultLocalTz,
      email: currentUser?.email ?? '',
    }),
    [currentUser?.email, defaultLocalTz]
  )

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors: formErrors },
  } = useForm({
    defaultValues,
    values: defaultValues,
  })
  const selectedTimeZone = useWatch({ control, name: 'timeZone' }) ?? defaultLocalTz

  const onClose = useCallback(() => {
    reset()
    handleClose()
  }, [handleClose, reset])

  const onSubmit = useCallback<(data: { timeZone: string; email: string }) => void>(
    (data) => {
      initiateBotDetectionEventsExport(
        {
          params: { subscriptionId },
          data: botDetectionEventsExportBodyAdapter({
            email: data.email,
            filterValues,
            dateRange,
            timeZone: data.timeZone,
          }),
        },
        {
          onSuccess: () => {
            showToast({
              severity: 'success',
              message: (
                <>
                  Success! Be on the lookout for a <strong>download link</strong> sent to <strong>{data.email}</strong>.
                </>
              ),
            })
            ampli.dataExportRequested({
              exportFormat: 'CSV',
              timeZone: data.timeZone === 'UTC' ? 'UTC' : 'Local Time',
              dateRange: 'Other',
              attributeFilters: Object.keys(filterValues) as DataExportRequestedProperties['attributeFilters'],
            })
            onClose()
          },
        }
      )
    },
    [dateRange, filterValues, initiateBotDetectionEventsExport, onClose, showToast, subscriptionId]
  )

  const emailInputError = getErrorParams('email', formErrors)
  const hasAppliedFilters = Object.keys(filterValues).length > 0

  return (
    <ModalOverlay
      isDismissable
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          onClose()
        }
      }}
    >
      <DialogContent size='lg'>
        <form onSubmit={handleSubmit(onSubmit)} className={contentStyles()}>
          <DialogHeader>
            <DialogTitle>Export events</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <Alert variant='info' icon={<FileSpreadsheet />}>
              This export will contain only the data included in your current plan.
            </Alert>
            <div className={defaultLocalTz?.toLowerCase() === 'utc' ? 'hidden' : undefined}>
              <Controller
                name='timeZone'
                control={control}
                render={({ field }) => (
                  <RadioGroup
                    label='Time zone'
                    value={field.value}
                    onChange={field.onChange}
                    classes={{ label: 'text-gray-800' }}
                    className='flex flex-col flex-nowrap'
                  >
                    <Radio value={defaultLocalTz}>Local Time ({defaultLocalTz})</Radio>
                    <Radio value='UTC'>UTC</Radio>
                  </RadioGroup>
                )}
              />
            </div>
            <div className='flex flex-col gap-2'>
              <Typography size='base' weight='medium' className='text-gray-800'>
                Date range
              </Typography>
              <Typography as='dd' size='base' weight='medium'>
                {formatDatetime(dateRange.since.toJSDate(), 'precise', selectedTimeZone, 'standardWithYear')}
                {` - ${formatDatetime(dateRange.before.toJSDate(), 'precise', selectedTimeZone, 'standardWithYear')}`}
              </Typography>
            </div>
            {hasAppliedFilters && (
              <div className='flex flex-col gap-2'>
                <Typography size='base' weight='medium' className='text-gray-800'>
                  Applied filters
                </Typography>
                <AppliedQueryFilters<BotDetectionEventsFilterValues>
                  filterDefinitions={filterDefinitions}
                  filterValues={filterValues}
                  isDisabled
                />
              </div>
            )}
            <TextField
              label='Send export link to'
              description="We'll send a download link to this email when the export is ready."
              id='email'
              tone='background1'
              defaultValue={defaultValues.email}
              {...compassRegister(register, 'email', EMAIL_VALIDATION)}
              {...(emailInputError.error && { ...emailInputError })}
              isInvalid={!!emailInputError.error}
              errorMessage={emailInputError.helperText}
              validationBehavior='aria'
              classes={{ description: 'text-xs', label: 'text-gray-800' }}
              className='w-full'
            />
          </DialogBody>

          <DialogFooter>
            <Button variant='secondary' onPress={onClose}>
              Cancel
            </Button>
            <Button type='submit' isDisabled={isPending} isLoading={isPending}>
              Start export
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </ModalOverlay>
  )
}

const convertDateWithZone = (date: DateTime, timeZone: string) => {
  const formattedDate = date.setZone(timeZone, { keepLocalTime: true }).toISO({ includeOffset: true })

  if (formattedDate) {
    return DateTime.fromISO(formattedDate).toUTC().toString()
  }

  return date.toISO() as string
}

function botDetectionEventsExportBodyAdapter({
  email,
  filterValues,
  dateRange,
  timeZone,
}: {
  email: string
  filterValues: Partial<BotDetectionEventsFilter>
  dateRange: { since: DateTime; before: DateTime }
  timeZone: string
}): BotDetectionEventsExportBody {
  const filters = Object.fromEntries(
    Object.entries(filterValues).filter(([, value]) => {
      if (Array.isArray(value)) {
        return value.length > 0
      }
      return value !== undefined
    })
  ) as BotDetectionEventsFilter

  return {
    email,
    dateRange: {
      from: convertDateWithZone(dateRange.since, timeZone),
      to: convertDateWithZone(dateRange.before, timeZone),
    },
    ...(Object.keys(filters).length > 0 ? { filters } : {}),
  }
}
